import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  BookHeart,
  Check,
  Eye,
  Pencil,
  RefreshCw,
  Search,
  ShieldAlert,
  Trash2,
  X
} from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { api, idempotencyKey } from "../api";
import "./memory.css";

type Source = {
  id?: string;
  sourceClient: "chatgpt" | "cyberboss";
  sourceThreadId?: string | null;
  sourceReference?: string | null;
  sourceSummary?: string | null;
  createdAt: string;
};

type MemoryItem = {
  id: string;
  title: string | null;
  content: string | null;
  kind: "fact" | "preference" | "instruction" | "context";
  status: "proposed" | "approved" | "rejected" | "archived" | "forgotten";
  importance: number;
  confidence: number;
  version: number;
  tags: string[];
  sources: Source[];
  rejectionReason?: string | null;
  updatedAt: string;
  forgottenAt?: string | null;
};

type EditForm = {
  title: string;
  content: string;
  kind: MemoryItem["kind"];
  importance: number;
  confidence: number;
  tags: string;
};

const kindLabels = {
  fact: "事实",
  preference: "偏好",
  instruction: "长期约定",
  context: "背景"
};

function toForm(item: MemoryItem): EditForm {
  return {
    title: item.title ?? "",
    content: item.content ?? "",
    kind: item.kind,
    importance: item.importance,
    confidence: item.confidence,
    tags: item.tags.join("，")
  };
}

function formChanges(form: EditForm) {
  return {
    title: form.title.trim(),
    content: form.content.trim(),
    kind: form.kind,
    importance: form.importance,
    confidence: form.confidence,
    tags: form.tags.split(/[，,]/).map((tag) => tag.trim()).filter(Boolean)
  };
}

type ConfidenceTier = "low" | "medium" | "high";
type MobileView = "list" | "detail";
type MemoryEditorHandle = { changes: () => ReturnType<typeof formChanges> };

const confidenceTiers: Record<ConfidenceTier, { label: string; representative: number }> = {
  low: { label: "低", representative: 25 },
  medium: { label: "中", representative: 60 },
  high: { label: "高", representative: 90 }
};

export function confidenceTierFor(value: number): ConfidenceTier {
  if (value < 40) return "low";
  if (value < 80) return "medium";
  return "high";
}

function usesCompactMobileLayout() {
  return typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 760px)").matches
    : window.innerWidth <= 760;
}

const MemoryEditor = forwardRef<MemoryEditorHandle, { item: MemoryItem }>(function MemoryEditor(
  { item },
  ref
) {
  const initial = toForm(item);
  const titleRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const tagsRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState(initial.kind);
  const [importance, setImportance] = useState(initial.importance);
  const [confidenceTier, setConfidenceTier] = useState<ConfidenceTier>(
    confidenceTierFor(initial.confidence)
  );
  const confidenceChanged = useRef(false);
  const effectiveConfidence = confidenceChanged.current
    ? confidenceTiers[confidenceTier].representative
    : initial.confidence;

  useImperativeHandle(ref, () => ({
    changes: () => formChanges({
      title: titleRef.current?.value ?? initial.title,
      content: contentRef.current?.value ?? initial.content,
      tags: tagsRef.current?.value ?? initial.tags,
      kind,
      importance,
      confidence: confidenceChanged.current
        ? confidenceTiers[confidenceTier].representative
        : initial.confidence
    })
  }), [confidenceTier, importance, initial.confidence, initial.content, initial.tags, initial.title, kind]);

  return (
    <div className="memory-form">
      <label>标题<input ref={titleRef} defaultValue={initial.title} /></label>
      <label>正文<textarea ref={contentRef} rows={8} defaultValue={initial.content} /></label>
      <div className="memory-fields">
        <label className="memory-kind">类型<select value={kind} onChange={(event) => setKind(event.target.value as MemoryItem["kind"])}>
          {Object.entries(kindLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select></label>
        <div className="memory-range">
          <div className="memory-field-label"><span>重要度</span><output>{importance}</output></div>
          <input aria-label="重要度" type="range" min="1" max="5" step="1" value={importance} onChange={(event) => setImportance(Number(event.target.value))} />
          <div className="memory-range-hints" aria-hidden="true"><span>低</span><span>高</span></div>
        </div>
        <fieldset className="memory-confidence">
          <legend><span>置信度</span><output>{confidenceTiers[confidenceTier].label} · {effectiveConfidence}</output></legend>
          <div>
            {(Object.entries(confidenceTiers) as Array<[ConfidenceTier, (typeof confidenceTiers)[ConfidenceTier]]>).map(([value, option]) => (
              <label key={value}>
                <input
                  type="radio"
                  name={`memory-confidence-${item.id}`}
                  value={value}
                  checked={confidenceTier === value}
                  onChange={() => {
                    confidenceChanged.current = true;
                    setConfidenceTier(value);
                  }}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <label>标签（逗号分隔）<input ref={tagsRef} defaultValue={initial.tags} /></label>
    </div>
  );
});

export function MemoryApprovalPage() {
  const [tab, setTab] = useState<"proposed" | "approved">("proposed");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>("list");
  const [searchVersion, setSearchVersion] = useState(0);
  const [items, setItems] = useState<MemoryItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [forgetPassword, setForgetPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const retryKeys = useRef(new Map<string, string>());
  const editorRef = useRef<MemoryEditorHandle>(null);
  const searchQueryRef = useRef("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchComposingRef = useRef(false);
  const listRef = useRef<HTMLElement>(null);
  const listPositionRef = useRef({ pageY: 0, listScrollTop: 0 });

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (tab === "proposed") params.set("status", "proposed");
      else params.set("include_archived", includeArchived ? "true" : "false");
      const searchQuery = searchQueryRef.current.trim();
      if (searchQuery) params.set("query", searchQuery);
      const result = await api<{ items: MemoryItem[] }>(`/memory/items?${params}`);
      const visible = tab === "approved"
        ? result.items.filter((item) => item.status === "approved" || (includeArchived && item.status === "archived"))
        : result.items;
      setItems(visible);
      setSelectedId((current) => visible.some((item) => item.id === current) ? current : null);
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "记忆列表加载失败。");
    } finally {
      setLoading(false);
    }
  }, [tab, includeArchived]);

  useEffect(() => { void load(); }, [load, searchVersion]);
  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);
  useEffect(() => {
    setRejectReason("");
    setForgetPassword("");
  }, [selected?.id, selected?.version]);
  useEffect(() => {
    if (!loading && mobileView === "detail" && !selected) {
      returnToList();
    }
  }, [loading, mobileView, selected]);


  function scheduleSearch(value: string) {
    searchQueryRef.current = value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      setSearchVersion((version) => version + 1);
    }, 300);
  }

  function refresh() {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = null;
    }
    void load();
  }

  function stableKey(action: string, item: MemoryItem) {
    const slot = `${item.id}:${item.version}:${action}`;
    let key = retryKeys.current.get(slot);
    if (!key) {
      key = idempotencyKey(`memory-${action}`);
      retryKeys.current.set(slot, key);
    }
    return key;
  }

  function openDetail(item: MemoryItem) {
    listPositionRef.current = {
      pageY: window.scrollY,
      listScrollTop: listRef.current?.scrollTop ?? 0
    };
    setSelectedId(item.id);
    setMobileView("detail");
    if (usesCompactMobileLayout()) window.scrollTo({ top: 0, behavior: "auto" });
  }

  function returnToList() {
    setMobileView("list");
    window.requestAnimationFrame(() => {
      if (listRef.current) listRef.current.scrollTop = listPositionRef.current.listScrollTop;
      window.scrollTo({ top: listPositionRef.current.pageY, behavior: "auto" });
    });
  }

  async function mutate(action: string, work: (key: string) => Promise<unknown>, item = selected) {
    if (!item) return;
    setBusy(action);
    setMessage("");
    try {
      await work(stableKey(action, item));
      retryKeys.current.delete(`${item.id}:${item.version}:${action}`);
      setMessage("已保存");
      await load();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "操作失败；若传输状态不明，可再次点同一按钮安全重试。");
    } finally {
      setBusy("");
    }
  }

  async function approve(withEdits: boolean) {
    if (!selected) return;
    const changes = withEdits ? editorRef.current?.changes() : undefined;
    await mutate(withEdits ? "edit-approve" : "approve", (key) =>
      api(`/memory/items/${encodeURIComponent(selected.id)}/decision`, {
        method: "POST",
        body: {
          decision: "approve",
          expected_version: selected.version,
          idempotency_key: key,
          ...(changes ? { changes } : {})
        }
      })
    );
  }

  async function reject() {
    if (!selected || !rejectReason.trim()) return;
    await mutate("reject", (key) =>
      api(`/memory/items/${encodeURIComponent(selected.id)}/decision`, {
        method: "POST",
        body: {
          decision: "reject",
          reason: rejectReason.trim(),
          expected_version: selected.version,
          idempotency_key: key
        }
      })
    );
  }

  async function saveApproved() {
    if (!selected) return;
    const changes = editorRef.current?.changes();
    if (!changes) return;
    await mutate("edit", (key) =>
      api(`/memory/items/${encodeURIComponent(selected.id)}`, {
        method: "PUT",
        body: {
          ...changes,
          expected_version: selected.version,
          idempotency_key: key
        }
      })
    );
  }

  async function transition(action: "archive" | "restore") {
    if (!selected) return;
    await mutate(action, (key) =>
      api(`/memory/items/${encodeURIComponent(selected.id)}/${action}`, {
        method: "POST",
        body: { expected_version: selected.version, idempotency_key: key }
      })
    );
  }

  async function forget() {
    if (!selected || !forgetPassword) return;
    if (!confirm("遗忘会清除当前数据库中的正文、修订、来源摘要／引用及专属标签。历史备份仍可能重新引入这些内容。确定继续吗？")) return;
    setBusy("forget");
    setMessage("");
    try {
      await api("/ai-tokens/step-up", { method: "POST", body: { password: forgetPassword } });
      await api(`/memory/items/${encodeURIComponent(selected.id)}/forget`, {
        method: "POST",
        body: {
          expected_version: selected.version,
          idempotency_key: stableKey("forget", selected)
        }
      });
      retryKeys.current.delete(`${selected.id}:${selected.version}:forget`);
      setForgetPassword("");
      setMessage("已遗忘；历史备份未被改写。");
      await load();
    } catch (reason) {
      setMessage(reason instanceof Error ? reason.message : "遗忘没有完成。");
    } finally {
      setBusy("");
    }
  }

  return (
    <div className={`memory-page memory-page--${mobileView}`}>
      <header className="memory-hero">
        <div>
          <p>PHASE 2 · SHARED MEMORY</p>
          <h1><BookHeart /> 记忆审批</h1>
          <span>只有你批准的内容会成为正式记忆；批准后 ChatGPT 与 Cyberboss 两端可见。</span>
        </div>
        <button className="button button--secondary memory-refresh" type="button" aria-label="刷新记忆" title="刷新" onClick={refresh} disabled={loading}>
          <RefreshCw size={18} aria-hidden="true" />
        </button>
      </header>

      <div className="memory-toolbar">
        <div className="memory-tabs">
          <button className={tab === "proposed" ? "active" : ""} onClick={() => setTab("proposed")}>待审批</button>
          <button className={tab === "approved" ? "active" : ""} onClick={() => setTab("approved")}>正式记忆</button>
        </div>
        <label className="memory-search"><Search size={17} aria-hidden="true" /><input
          defaultValue=""
          onChange={(event) => {
            searchQueryRef.current = event.currentTarget.value;
            if (!searchComposingRef.current) scheduleSearch(event.currentTarget.value);
          }}
          onCompositionStart={() => {
            searchComposingRef.current = true;
            if (searchTimerRef.current) {
              clearTimeout(searchTimerRef.current);
              searchTimerRef.current = null;
            }
          }}
          onCompositionEnd={(event) => {
            searchComposingRef.current = false;
            scheduleSearch(event.currentTarget.value);
          }}
          placeholder="搜索标题或正文"
        /></label>
        {tab === "approved" && (
          <label className="memory-archive-filter">
            <input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} />
            查看已归档
          </label>
        )}
      </div>

      {message && <div className="memory-message" role="status">{message}</div>}
      <div className={`memory-layout memory-layout--${mobileView}`}>
        <section ref={listRef} className="memory-list" aria-busy={loading} aria-label="记忆列表">
          {loading && !items.length && <p className="memory-empty">正在加载…</p>}
          {!loading && !items.length && <p className="memory-empty">这里暂时没有记录。</p>}
          {items.map((item) => (
            <button
              key={item.id}
              className={item.id === selectedId ? "selected" : ""}
              onClick={() => openDetail(item)}
              aria-label={`打开记忆：${item.title || "已遗忘的记忆"}，${item.status === "proposed" ? "待审批" : item.status === "archived" ? "已归档" : "已批准"}，${kindLabels[item.kind]}`}
            >
              <span className="memory-list__main">
                <strong>{item.title || "已遗忘的记忆"}</strong>
                <small>{kindLabels[item.kind]} · 更新于 {new Date(item.updatedAt).toLocaleString()}</small>
              </span>
              <span className="memory-list__aside">
                <span className={`memory-status memory-status--${item.status}`}>{item.status === "proposed" ? "待审批" : item.status === "archived" ? "已归档" : "已批准"}</span>
                {item.tags.length > 0 && (
                  <span className="memory-list__tags">
                    {item.tags.slice(0, 3).map((tag) => <i key={tag}>{tag}</i>)}
                    {item.tags.length > 3 && <i>+{item.tags.length - 3}</i>}
                  </span>
                )}
              </span>
            </button>
          ))}
        </section>

        <section className="memory-detail" aria-label="记忆详情">
          <button className="memory-detail__back" type="button" onClick={returnToList}>
            <ArrowLeft size={18} aria-hidden="true" /> 返回记忆列表
          </button>
          {!selected ? (
            <div className="memory-empty"><Eye />选择一条记忆查看详情</div>
          ) : (
            <>
              <div className="memory-detail__head">
                <div><span>{kindLabels[selected.kind]} · v{selected.version}</span><h2>{selected.title}</h2></div>
                <span className={`memory-status memory-status--${selected.status}`}>{selected.status}</span>
              </div>
              <div className="memory-provenance">
                <strong>有限来源信息</strong>
                {selected.sources.map((source, index) => (
                  <p key={source.id ?? index}>
                    {source.sourceClient} · {new Date(source.createdAt).toLocaleString()}
                    {source.sourceSummary ? ` · ${source.sourceSummary}` : ""}
                    {source.sourceReference ? ` · ${source.sourceReference}` : ""}
                  </p>
                ))}
              </div>
              <MemoryEditor key={selected.id + ":" + selected.version} ref={editorRef} item={selected} />

              {selected.status === "proposed" && (
                <>
                  <div className="memory-actions">
                    <button className="button button--primary" onClick={() => void approve(false)} disabled={Boolean(busy)}><Check />批准原文</button>
                    <button className="button button--secondary" onClick={() => void approve(true)} disabled={Boolean(busy)}><Pencil />修改并批准</button>
                  </div>
                  <div className="memory-reject">
                    <input value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="拒绝原因（必填）" />
                    <button className="button button--ghost" onClick={() => void reject()} disabled={Boolean(busy) || !rejectReason.trim()}><X />拒绝</button>
                  </div>
                </>
              )}

              {selected.status === "approved" && (
                <div className="memory-actions">
                  <button className="button button--primary" onClick={() => void saveApproved()} disabled={Boolean(busy)}><Pencil />保存为新修订</button>
                  <button className="button button--secondary" onClick={() => void transition("archive")} disabled={Boolean(busy)}><Archive />归档</button>
                </div>
              )}
              {selected.status === "archived" && (
                <button className="button button--primary" onClick={() => void transition("restore")} disabled={Boolean(busy)}><ArchiveRestore />恢复为正式记忆</button>
              )}

              {selected.status !== "forgotten" && (
                <div className="memory-forget">
                  <div><ShieldAlert /><p><strong>遗忘不会改写历史备份</strong><span>旧备份恢复后可能重新出现此内容；本功能不承诺磁盘级擦除。</span></p></div>
                  <label>当前登录密码<input type="password" value={forgetPassword} onChange={(event) => setForgetPassword(event.target.value)} /></label>
                  <button className="button button--danger" onClick={() => void forget()} disabled={Boolean(busy) || !forgetPassword}><Trash2 />最终确认遗忘</button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
